/**
 * PATH: apps/hr/src/pages/inbox/InboxSubcomponents.tsx
 * FILE_ID: HR-INBOX-SUBCOMPONENTS-001
 * PURPOSE: Presentational subcomponents for the supervisor approval
 *          queue — confidence pill, row, bulk action bar, empty
 *          state. Stateless except for local UI state (e.g. reject
 *          reason text); all data comes in via props.
 *
 * WHY: Phase 3 of HR_UNIFICATION_PLAN.md. Splitting these out keeps
 *      InboxPage.tsx focused on data fetching + state orchestration.
 */
import { useState } from 'react'
import { CheckCircle2, AlertCircle, AlertTriangle, ShieldAlert, Clock } from 'lucide-react'
import {
  confidenceBand,
  describeConfidence,
  formatRowSummary,
  formatSubmittedAt,
} from './inboxHelpers'
import type { InboxItem } from './inboxTypes'

// =============================================================================
// CONFIDENCE PILL
// =============================================================================

export function ConfidencePill({ item }: { item: InboxItem }) {
  const band = confidenceBand(item.confidence)
  const colour =
    band === 'green'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : band === 'amber'
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-rose-50 text-rose-700 border-rose-200'
  const Icon =
    band === 'green' ? CheckCircle2 : band === 'amber' ? AlertTriangle : ShieldAlert
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border ${colour}`}
      title={describeConfidence(item)}
    >
      <Icon className="w-3 h-3" />
      {Math.round(item.confidence * 100)}%
    </span>
  )
}

// =============================================================================
// EMPTY STATE
// =============================================================================

export function InboxEmpty({ status }: { status: string }) {
  return (
    <div className="text-center py-12 px-4">
      <Clock className="w-10 h-10 mx-auto text-slate-300" />
      <h3 className="mt-3 text-sm font-medium text-slate-700">
        Nothing in the {status} queue
      </h3>
      <p className="mt-1 text-xs text-slate-500">
        New submissions will appear here, amber/red rows first.
      </p>
    </div>
  )
}

// =============================================================================
// FEATURE-DISABLED NOTICE
// =============================================================================

export function FeatureDisabledNotice() {
  return (
    <div className="max-w-xl mx-auto mt-8 p-6 rounded-lg border border-slate-200 bg-white">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-slate-400 mt-0.5" />
        <div>
          <h2 className="text-base font-semibold text-slate-800">
            Approval queue not enabled
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            The supervisor approval queue is gated by the{' '}
            <code className="bg-slate-100 rounded px-1">approvals</code> HR
            feature flag. Ask an admin to enable it for this organisation
            in 0x3admin.
          </p>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// BULK ACTION BAR
// =============================================================================

interface BulkActionBarProps {
  selectedCount: number
  busy: boolean
  onApprove: () => void
  onReject: (reason: string) => void
  onClear: () => void
}

export function BulkActionBar({
  selectedCount,
  busy,
  onApprove,
  onReject,
  onClear,
}: BulkActionBarProps) {
  const [showReject, setShowReject] = useState(false)
  const [reason, setReason] = useState('')

  if (selectedCount === 0) return null

  const handleReject = () => {
    if (!reason.trim()) {
      return
    }
    onReject(reason.trim())
    setReason('')
    setShowReject(false)
  }

  return (
    <div className="sticky top-0 z-10 bg-teal-50 border border-teal-200 rounded-lg p-3 mb-3 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-teal-900">
          <span className="font-medium">{selectedCount}</span> day
          {selectedCount === 1 ? '' : 's'} selected
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClear}
            disabled={busy}
            className="text-xs text-teal-700 underline-offset-2 hover:underline disabled:opacity-50"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={onApprove}
            disabled={busy}
            className="px-3 py-1.5 text-sm font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            Approve all
          </button>
          <button
            type="button"
            onClick={() => setShowReject((s) => !s)}
            disabled={busy}
            className="px-3 py-1.5 text-sm font-medium rounded-md bg-white text-rose-700 border border-rose-300 hover:bg-rose-50 disabled:opacity-50"
          >
            Reject all
          </button>
        </div>
      </div>
      {showReject && (
        <div className="mt-3 flex gap-2">
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (required)"
            className="flex-1 text-sm border border-slate-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-rose-300"
          />
          <button
            type="button"
            onClick={handleReject}
            disabled={busy || !reason.trim()}
            className="px-3 py-1.5 text-sm font-medium rounded-md bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
          >
            Confirm reject
          </button>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// ROW
// =============================================================================

interface InboxRowProps {
  item: InboxItem
  selected: boolean
  busy: boolean
  onToggle: (id: string) => void
  onApprove: (id: string) => void
  onReject: (id: string) => void
}

export function InboxRow({
  item,
  selected,
  busy,
  onToggle,
  onApprove,
  onReject,
}: InboxRowProps) {
  return (
    <li
      className={`flex items-center gap-3 px-3 py-2.5 border-b border-slate-100 ${
        selected ? 'bg-teal-50/50' : 'bg-white hover:bg-slate-50'
      }`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={() => onToggle(item.id)}
        className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
        aria-label={`Select ${item.worker_name} ${item.work_date}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-800 truncate">
            {item.worker_name}
          </span>
          <ConfidencePill item={item} />
        </div>
        <div className="mt-0.5 text-xs text-slate-500 flex items-center gap-2 flex-wrap">
          <span>{item.work_date}</span>
          <span aria-hidden="true">·</span>
          <span>{formatRowSummary(item)}</span>
          <span aria-hidden="true">·</span>
          <span>Submitted {formatSubmittedAt(item.submitted_at)}</span>
        </div>
        {item.anomaly_reasons.length > 0 && (
          <div className="mt-1 text-xs text-amber-700">
            {item.anomaly_reasons.join(' · ')}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onApprove(item.id)}
        disabled={busy}
        className="px-2.5 py-1 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
      >
        Approve
      </button>
      <button
        type="button"
        onClick={() => onReject(item.id)}
        disabled={busy}
        className="px-2.5 py-1 text-xs font-medium rounded-md bg-white text-rose-700 border border-rose-300 hover:bg-rose-50 disabled:opacity-50"
      >
        Reject
      </button>
    </li>
  )
}
